import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, Plus, Trash2, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { compressImage } from "../lib/smartLotto";
import { Button, Field, Input } from "./ui-kit";

export const EMPTY_DELIVERY_DOCUMENT = { kind: "ddt", number: "", documents: [], pending: false };
const KINDS = [["ddt", "DDT"], ["fattura", "Fattura"]];
const MAX_PAGES = 15;

export const deliveryDocumentReady = (value) => Boolean(value?.documents?.length || value?.pending);

/**
 * Documento di accompagnamento (DDT o fattura) di un ricevimento merci.
 * Si fotografano le pagine una alla volta e il server le unisce in un unico PDF A4;
 * in alternativa si carica un PDF già pronto. Senza documento si può proseguire
 * solo dichiarando che verrà allegato in seguito (allowPending).
 */
export default function DeliveryDocument({ value, onChange, allowPending = true, showNumber = true, testid = "delivery-doc" }) {
  const camera = useRef(null);
  const fileInput = useRef(null);
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const label = value.kind === "fattura" ? "fattura" : "DDT";
  useEffect(() => () => pages.forEach((page) => URL.revokeObjectURL(page.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => onChange({ ...value, ...patch });
  const addPages = (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith("image/"));
    if (!files.length) return;
    setPages((current) => {
      const room = MAX_PAGES - current.length;
      if (files.length > room) toast.error(`Massimo ${MAX_PAGES} pagine per documento`);
      return [...current, ...files.slice(0, Math.max(room, 0)).map((file) => ({ file, preview: URL.createObjectURL(file) }))];
    });
  };
  const removePage = (index) => {
    if (!window.confirm(`Vuoi eliminare la pagina ${index + 1} fotografata?`)) return;
    setPages((current) => {
      URL.revokeObjectURL(current[index].preview);
      return current.filter((_, i) => i !== index);
    });
  };
  const removeDocument = (index) => {
    if (!window.confirm(`Vuoi eliminare questo ${label}?\n${value.documents[index].name}\n\nPotrai fotografarlo o caricarlo di nuovo.`)) return;
    set({ documents: value.documents.filter((_, n) => n !== index) });
  };
  const savePdf = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      for (const [n, page] of pages.entries()) fd.append("files", await compressImage(page.file, 1754, 0.8), `pagina-${n + 1}.jpg`);
      const { data } = await api.post(`/app/catalog/receipts/scan?kind=${value.kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      pages.forEach((page) => URL.revokeObjectURL(page.preview));
      setPages([]);
      set({ documents: [...value.documents, { url: data.url, name: data.name }], pending: false });
      toast.success(`${value.kind === "fattura" ? "Fattura" : "DDT"} salvato in PDF (${data.pages} ${data.pages === 1 ? "pagina" : "pagine"})`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const uploadFile = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/app/catalog/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, name: data.name });
      }
      set({ documents: [...value.documents, ...added], pending: false });
      toast.success(added.length > 1 ? `${added.length} file allegati` : "Documento allegato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex gap-2" role="group" aria-label="Tipo di documento">
        {KINDS.map(([kind, text]) => (
          <button key={kind} type="button" onClick={() => set({ kind })} aria-pressed={value.kind === kind}
            className={`min-h-[44px] flex-1 rounded-lg border-2 px-3 text-sm font-semibold ${value.kind === kind ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
            data-testid={`${testid}-kind-${kind}`}>{text}</button>
        ))}
      </div>
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPages(e.target.files); e.target.value = ""; }} data-testid={`${testid}-camera`} />
      <input ref={fileInput} type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={(e) => { uploadFile(e.target.files); e.target.value = ""; }} data-testid={`${testid}-file`} />

      {value.documents.length > 0 && (
        <div className="space-y-1" data-testid={`${testid}-saved`}>
          {value.documents.map((doc, i) => (
            <div key={doc.url} className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <a href={doc.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 font-medium underline"><FileText className="h-4 w-4 shrink-0" /><span className="truncate">{doc.name}</span></a>
              <button type="button" onClick={() => removeDocument(i)} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg border-2 border-red-200 bg-white px-3 text-sm font-semibold text-red-700 hover:border-red-400 hover:bg-red-50" aria-label={`Elimina ${doc.name}`} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /> Elimina</button>
            </div>
          ))}
        </div>
      )}

      {!value.pending && (
        <>
          {pages.length === 0 ? (
            <Button variant={value.documents.length ? "outline" : "primary"} className="w-full !min-h-[56px]" disabled={busy} onClick={() => camera.current?.click()} data-testid={`${testid}-shoot`}>
              <Camera className="h-5 w-5" /> {value.documents.length ? `Fotografa un altro documento` : `Fotografa il ${label}`}
            </Button>
          ) : (
            <div className="space-y-2 rounded-xl border border-hairline bg-white p-3" data-testid={`${testid}-pages`}>
              <p className="text-sm font-medium text-navy">{pages.length} {pages.length === 1 ? "pagina fotografata" : "pagine fotografate"}: se il documento ha altre pagine fotografale ora.</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {pages.map((page, i) => (
                  <div key={page.preview} className="relative">
                    <img src={page.preview} alt={`Pagina ${i + 1}`} className="aspect-[3/4] w-full rounded-lg border border-hairline object-cover" />
                    <span className="absolute left-1 top-1 rounded bg-navy/80 px-1.5 text-[11px] font-semibold text-white">{i + 1}</span>
                    <button type="button" onClick={() => removePage(i)} className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-red-600 shadow" aria-label={`Togli pagina ${i + 1}`}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="outline" className="flex-1" disabled={busy || pages.length >= MAX_PAGES} onClick={() => camera.current?.click()} data-testid={`${testid}-add-page`}><Plus className="h-4 w-4" /> Aggiungi pagina</Button>
                <Button variant="success" className="flex-1" loading={busy} onClick={savePdf} data-testid={`${testid}-save-pdf`}><FileText className="h-4 w-4" /> Salva {label} in PDF</Button>
              </div>
            </div>
          )}
          {pages.length === 0 && (
            <button type="button" disabled={busy} onClick={() => fileInput.current?.click()} className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-navy underline disabled:opacity-50" data-testid={`${testid}-upload`}>
              <Upload className="h-4 w-4" /> Hai già il PDF (es. ricevuto via email)? Carica il file
            </button>
          )}
        </>
      )}

      {allowPending && value.documents.length === 0 && pages.length === 0 && (
        <label className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 text-sm ${value.pending ? "border-amber-400 bg-amber-50 text-amber-900" : "border-red-500 bg-red-50 text-red-800"}`} data-testid={`${testid}-pending`}>
          <input type="checkbox" checked={value.pending} onChange={(e) => set({ pending: e.target.checked })} className={`mt-0.5 h-5 w-5 shrink-0 ${value.pending ? "accent-amber-600" : "accent-red-600"}`} data-testid={`${testid}-pending-check`} />
          <span><span className="font-semibold">Non ho il documento di accompagnamento adesso: lo allegherò in seguito.</span>
            {value.pending
              ? <span className="mt-1 block text-xs">Il ricevimento verrà registrato con il {label} da allegare: lo ritrovi nel Check-up e nel registro ricevimenti.</span>
              : <span className="mt-1 block text-xs">Per andare avanti fotografa o carica {label === "fattura" ? "la fattura" : "il DDT"}; se non ce l'hai adesso, spunta questa casella.</span>}</span>
        </label>
      )}

      {showNumber && (
        <Field label={`Numero ${label} (facoltativo)`}>
          <Input value={value.number} onChange={(e) => set({ number: e.target.value.toUpperCase() })} placeholder="Es. 1234/2026" data-testid={`${testid}-number`} />
        </Field>
      )}
    </div>
  );
}
